import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Cross, Menu, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import api from "@/lib/api";

export const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/history", label: "Church History" },
  { to: "/leadership", label: "Leadership" },
  { to: "/services", label: "Worship & Services" },
  { to: "/church-family", label: "Church Family" },
  { to: "/ministries", label: "Ministries" },
  { to: "/events", label: "Events" },
  { to: "/announcements", label: "Announcements" },
  { to: "/gallery", label: "Photo Gallery" },
];

export default function PublicLayout() {
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const name = settings?.church_name || "Christ Church CNI, Rajpura";

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <header className="sticky top-0 z-50 border-b border-navy/10 bg-white/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="site-logo">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy text-gold">
              <Cross className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="min-w-0">
              <span className="block truncate font-heading text-base sm:text-lg font-bold text-navy leading-tight">
                Christ Church CNI
              </span>
              <span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-gold-dark">
                Rajpura · Chandigarh Diocese
              </span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-5" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold transition-colors ${
                    isActive ? "text-gold-dark" : "text-navy/70 hover:text-navy"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Button
              asChild
              className="hidden sm:inline-flex bg-gold text-navy hover:bg-gold-dark hover:text-cream transition-colors"
              data-testid="nav-contact-btn"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="xl:hidden" data-testid="mobile-menu-btn" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-cream">
                <div className="mt-8 flex flex-col gap-1">
                  {[...NAV_ITEMS, { to: "/contact", label: "Contact Us" }].map((item) => (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={({ isActive }) =>
                          `rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
                            isActive ? "bg-navy text-cream" : "text-navy/80 hover:bg-secondary"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet context={{ settings }} />
      </main>

      <footer className="bg-navy text-cream grain-overlay">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold text-navy">
                <Cross className="h-5 w-5" strokeWidth={2.5} />
              </span>
              <div>
                <p className="font-heading text-lg font-bold">Christ Church CNI</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-gold">Rajpura, Punjab</p>
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-cream/70">
              {settings?.tagline || "A warm and welcoming family of faith."}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">Visit</p>
            <ul className="space-y-2 text-sm text-cream/80">
              {NAV_ITEMS.slice(0, 6).map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="hover:text-gold transition-colors">{i.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">Connect</p>
            <ul className="space-y-2 text-sm text-cream/80">
              {NAV_ITEMS.slice(6).map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="hover:text-gold transition-colors">{i.label}</Link>
                </li>
              ))}
              <li>
                <Link to="/contact" className="hover:text-gold transition-colors">Contact Us</Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">Contact</p>
            <ul className="space-y-3 text-sm text-cream/80">
              <li className="flex gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gold" /><span>{settings?.address || "[Placeholder] Church address"}</span></li>
              <li className="flex gap-2"><Phone className="h-4 w-4 mt-0.5 shrink-0 text-gold" /><span>{settings?.phone || "[Placeholder] Phone"}</span></li>
              <li className="flex gap-2"><Mail className="h-4 w-4 mt-0.5 shrink-0 text-gold" /><span>{settings?.email || "[Placeholder] Email"}</span></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-cream/10">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 py-6 sm:flex-row sm:px-8">
            <p className="text-xs text-cream/50">
              © {new Date().getFullYear()} {name}. Church of North India · Chandigarh Diocese.
            </p>
            <Link
              to="/admin/login"
              data-testid="admin-login-link"
              className="text-xs text-cream/35 hover:text-gold transition-colors"
            >
              Admin Login
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
